import { BusinessOpeningHour } from '@/types/index';
import { convertUtcDateToBusinessTimezone } from '../core/timezone-utils';

interface BusinessStatus {
  isOpen: boolean;
  statusText: string;
}

export function getBusinessStatus(
  openingHours: BusinessOpeningHour[] | null,
  currentTime?: Date,
  businessTimezone?: string
): BusinessStatus {
  const utcNow = currentTime || new Date();

  // Convert to business timezone if provided
  const now = businessTimezone
    ? convertUtcDateToBusinessTimezone(utcNow, businessTimezone)
    : utcNow;

  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const currentDayName = dayNames[now.getDay()]; // Get day name string in business timezone
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();


  if (!openingHours || openingHours.length === 0) {
    return { isOpen: false, statusText: "Hours not set" };
  }

  const todayHours = openingHours.find(
    (hour) => hour.day_of_week === currentDayName // Compare with day name
  );


  if (!todayHours) {
    return { isOpen: false, statusText: "Closed" };
  }

  if (todayHours.is_closed_all_day) {
    return { isOpen: false, statusText: "Closed all day" };
  }
  
  if (todayHours.is_24_hours) {
    return { isOpen: true, statusText: "Open 24 hours" };
  }

  const parseTime = (timeString: string | null) => {
    if (!timeString) return null;
    const [hour, minute] = timeString.split(':').map(Number);
    return hour * 60 + minute; // Convert to minutes since midnight
  };

  const openMinutes = parseTime(todayHours.opening_time);
  const closeMinutes = parseTime(todayHours.closing_time);
  const currentMinutes = currentHour * 60 + currentMinute;

  if (openMinutes === null || closeMinutes === null) {
    return { isOpen: false, statusText: "Hours invalid" };
  }

  let isOpen = false;
  let statusText = "Closed";

  // Handle overnight hours (e.g., 22:00 - 02:00)
  if (openMinutes < closeMinutes) {
    // Normal hours within the same day
    isOpen = currentMinutes >= openMinutes && currentMinutes <= closeMinutes;
  } else {
    // Overnight hours (e.g., opens at 22:00, closes at 02:00 next day)
    isOpen = currentMinutes >= openMinutes || currentMinutes <= closeMinutes;
  }

  if (isOpen) {
    statusText = "Open";

    // Check if closing soon (e.g., within the next 30 minutes)
    const closingSoonThreshold = 30; // minutes
    if (closeMinutes - currentMinutes <= closingSoonThreshold && closeMinutes - currentMinutes > 0) {
      statusText = `Closing in ${closeMinutes - currentMinutes} min`;
    }
  }

  return { isOpen, statusText };
}

/**
 * Calculate time overlap between a time range and business hours
 * @param shiftStartTime - Start time of the shift
 * @param shiftEndTime - End time of the shift
 * @param businessHours - Business opening hours for the day
 * @param businessTimezone - Business timezone for accurate time calculations
 * @returns Object with overlap information
 */
export function calculateTimeOverlapWithBusinessHours(
  shiftStartTime: Date,
  shiftEndTime: Date,
  businessHours: BusinessOpeningHour | null,
  businessTimezone?: string
): {
  totalMinutes: number;
  insideBusinessMinutes: number;
  outsideBusinessMinutes: number;
  percentageOutside: number;
} {
  const totalMinutes = Math.max(0, Math.floor((shiftEndTime.getTime() - shiftStartTime.getTime()) / (1000 * 60)));
  
  if (!businessHours || businessHours.is_closed_all_day || !businessHours.is_open) {
    // If business is closed all day, all shift time is outside business hours
    return {
      totalMinutes,
      insideBusinessMinutes: 0,
      outsideBusinessMinutes: totalMinutes,
      percentageOutside: totalMinutes > 0 ? 100 : 0
    };
  }

  if (businessHours.is_24_hours) {
    // If business is open 24 hours, all shift time is inside business hours
    return {
      totalMinutes,
      insideBusinessMinutes: totalMinutes,
      outsideBusinessMinutes: 0,
      percentageOutside: 0
    };
  }

  const parseTimeToMinutes = (timeString: string | null): number | null => {
    if (!timeString) return null;
    const [hour, minute] = timeString.split(':').map(Number);
    return hour * 60 + minute;
  };

  const businessStartMinutes = parseTimeToMinutes(businessHours.opening_time);
  const businessEndMinutes = parseTimeToMinutes(businessHours.closing_time);
  
  if (businessStartMinutes === null || businessEndMinutes === null) {
    // Invalid business hours, treat as closed
    return {
      totalMinutes,
      insideBusinessMinutes: 0,
      outsideBusinessMinutes: totalMinutes,
      percentageOutside: totalMinutes > 0 ? 100 : 0
    };
  }

  // Convert shift times to minutes since start of day in business timezone
  const businessShiftStart = businessTimezone
    ? convertUtcDateToBusinessTimezone(shiftStartTime, businessTimezone)
    : shiftStartTime;
  const businessShiftEnd = businessTimezone
    ? convertUtcDateToBusinessTimezone(shiftEndTime, businessTimezone)
    : shiftEndTime;

  const shiftStartMinutes = businessShiftStart.getHours() * 60 + businessShiftStart.getMinutes();
  const shiftEndMinutes = businessShiftEnd.getHours() * 60 + businessShiftEnd.getMinutes();

  let insideBusinessMinutes = 0;

  if (businessStartMinutes <= businessEndMinutes) {
    // Normal business hours (e.g., 9:00 AM - 5:00 PM)
    const overlapStart = Math.max(shiftStartMinutes, businessStartMinutes);
    const overlapEnd = Math.min(shiftEndMinutes, businessEndMinutes);
    insideBusinessMinutes = Math.max(0, overlapEnd - overlapStart);
  } else {
    // Overnight business hours (e.g., 10:00 PM - 2:00 AM next day)
    // This is handled by checking if shift overlaps with late night portion or early morning portion
    const lateNightOverlap = Math.max(0, 
      Math.min(shiftEndMinutes, 24 * 60) - Math.max(shiftStartMinutes, businessStartMinutes)
    );
    const earlyMorningOverlap = Math.max(0,
      Math.min(shiftEndMinutes, businessEndMinutes) - Math.max(shiftStartMinutes, 0)
    );
    insideBusinessMinutes = lateNightOverlap + earlyMorningOverlap;
  }

  const outsideBusinessMinutes = Math.max(0, totalMinutes - insideBusinessMinutes);
  const percentageOutside = totalMinutes > 0 ? Math.round((outsideBusinessMinutes / totalMinutes) * 100) : 0;

  return {
    totalMinutes,
    insideBusinessMinutes,
    outsideBusinessMinutes,
    percentageOutside
  };
}

/**
 * Calculate total hours worked outside business hours for multiple shifts
 * @param shifts - Array of shift data with employee info
 * @param businessOpeningHours - Business opening hours by day
 * @param selectedDate - The date to calculate for
 * @param businessTimezone - Business timezone for accurate time calculations
 * @returns Summary of outside hours worked and scheduled
 */
export function calculateOutsideBusinessHours(
  shifts: Array<{
    startTime?: string | Date;
    endTime?: string | Date;
    actualArrivalTime?: string | Date;
    actualDepartureTime?: string | Date;
  }>,
  businessOpeningHours: BusinessOpeningHour[],
  selectedDate: Date,
  businessTimezone?: string
): {
  scheduledOutsideMinutes: number;
  workedOutsideMinutes: number;
  scheduledOutsideFormatted: string;
  workedOutsideFormatted: string;
} {
  // Convert selectedDate to business timezone for day determination
  const businessDate = businessTimezone
    ? convertUtcDateToBusinessTimezone(selectedDate, businessTimezone)
    : selectedDate;

  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const dayName = dayNames[businessDate.getDay()];
  
  // Find business hours for this day of week
  const dayBusinessHours = businessOpeningHours.find(
    hour => hour.day_of_week === dayName
  );

  let scheduledOutsideMinutes = 0;
  let workedOutsideMinutes = 0;

  for (const shift of shifts) {
    if (!shift.startTime || !shift.endTime) continue;

    const shiftStart = new Date(shift.startTime);
    const shiftEnd = new Date(shift.endTime);

    // Calculate scheduled time outside business hours
    const scheduledOverlap = calculateTimeOverlapWithBusinessHours(
      shiftStart,
      shiftEnd,
      dayBusinessHours || null,
      businessTimezone
    );
    scheduledOutsideMinutes += scheduledOverlap.outsideBusinessMinutes;

    // Calculate actual worked time outside business hours if employee worked
    if (shift.actualArrivalTime && shift.actualDepartureTime) {
      const actualStart = new Date(shift.actualArrivalTime);
      const actualEnd = new Date(shift.actualDepartureTime);
      
      const workedOverlap = calculateTimeOverlapWithBusinessHours(
        actualStart,
        actualEnd,
        dayBusinessHours || null,
        businessTimezone
      );
      workedOutsideMinutes += workedOverlap.outsideBusinessMinutes;
    } else if (shift.actualArrivalTime && !shift.actualDepartureTime) {
      // If still working, calculate up to current time (only for today)
      const utcNow = new Date();
      const businessNow = businessTimezone
        ? convertUtcDateToBusinessTimezone(utcNow, businessTimezone)
        : utcNow;

      // Compare dates in business timezone
      if (businessNow.toDateString() === businessDate.toDateString()) {
        const actualStart = new Date(shift.actualArrivalTime);

        const workedOverlap = calculateTimeOverlapWithBusinessHours(
          actualStart,
          businessNow,
          dayBusinessHours || null,
          businessTimezone
        );
        workedOutsideMinutes += workedOverlap.outsideBusinessMinutes;
      }
    }
  }

  // Format as HH:MM
  const formatMinutes = (totalMinutes: number) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  return {
    scheduledOutsideMinutes,
    workedOutsideMinutes,
    scheduledOutsideFormatted: formatMinutes(scheduledOutsideMinutes),
    workedOutsideFormatted: formatMinutes(workedOutsideMinutes)
  };
}
